import type { Primitive, UnionToIntersection } from 'type-fest';
import type * as z from 'zod/v4';

type IterateOnTuple<TTuple extends [...unknown[]]> = TTuple extends [infer Head, ...infer Tail]
  ? [Unbrand<Head>, ...IterateOnTuple<Tail>]
  : [];

type RemoveBrand<TPrimitive> =
  TPrimitive extends z.BRAND<infer Brand>
    ? TPrimitive extends (z.BRAND<Brand> | UnionToIntersection<{ [K in Brand]: z.BRAND<K> }[Brand]>) & infer X
      ? RemoveBrand<X>
      : never
    : TPrimitive;

/**
 * Recursively removes the brand from T.
 *
 * @see https://github.com/colinhacks/zod/discussions/1994#discussioncomment-6068940
 */
export type Unbrand<TType> = TType extends Primitive
  ? RemoveBrand<TType>
  : TType extends Promise<infer E>
    ? Promise<Unbrand<E>>
    : TType extends [unknown, ...unknown[]]
      ? IterateOnTuple<RemoveBrand<TType>>
      : TType extends (infer E)[]
        ? Unbrand<E>[]
        : TType extends Set<infer E>
          ? Set<Unbrand<E>>
          : TType extends Map<infer E, infer F>
            ? Map<Unbrand<E>, Unbrand<F>>
            : { [K in keyof TType]: Unbrand<TType[K]> };

/**
 * Util to create branded data without type casting every single value in complex data structures like arrays or objects.
 * Useful for data mocking in unit tests, storybook stories, etc.
 *
 * Provide the generic type of the data you want to brand.
 *
 * @param payload Unbranded payload to brand
 * @returns Branded payload
 */
export function createBrandedData<TType>(payload: Unbrand<TType>): TType {
  return payload as TType;
}

/**
 * Removes branded type.
 *
 * @param payload item to remove branded type from
 * @returns unbranded type
 */
export function unbrand<TValue>(payload: TValue): Unbrand<TValue> {
  return payload as unknown as Unbrand<TValue>;
}
